import type { AppNotification } from '../types';

export const DISPLAY_PENDING_APPROVAL = 'display.pending_approval';
/** A display reported a fault (playback, content, connection, command…). */
export const DISPLAY_FAULT = 'display.fault';

export interface NotificationContent {
	title: string;
	/** Plain-text summary built from `data`; `null` when the type is unknown. */
	description: string | null;
	/** In-app path to open, only when it is a safe relative path. */
	link: string | null;
	/** Label of the inline action (e.g. "Review"), when the type has one. */
	actionLabel: string | null;
}

function text(value: unknown): string | null {
	return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Only same-app paths are followed: an absolute or protocol-relative URL (or
 * `javascript:`) in notification data must never become a navigation target.
 */
export function safeAppPath(value: unknown): string | null {
	const path = text(value);
	if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
	return path;
}

/**
 * What the UI shows for a notification. Built from `type` + `data` rather than
 * the server's HTML body, so nothing server-rendered is injected into the DOM.
 */
export function notificationContent(
	notification: AppNotification,
): NotificationContent {
	const { data } = notification;
	const link = safeAppPath(data.link);

	if (notification.type === DISPLAY_PENDING_APPROVAL) {
		const device = [text(data.platform), text(data.model)]
			.filter(Boolean)
			.join(' · ');
		const by = text(data.registeredBy);
		const parts = [
			device ? `${device} display` : 'A display',
			by ? `registered by ${by}` : 'was registered',
			'and is waiting for approval.',
		];
		return {
			title: notification.title,
			description: parts.join(' '),
			link,
			actionLabel: link ? 'Review' : null,
		};
	}

	if (notification.type === DISPLAY_FAULT) {
		const parts = [
			text(data.errorCode) ? `Error ${text(data.errorCode)}` : null,
			text(data.detail),
		].filter(Boolean);
		return {
			title: notification.title,
			description: parts.length > 0 ? parts.join(' · ') : null,
			link,
			actionLabel: link ? 'Open display' : null,
		};
	}

	return {
		title: notification.title,
		description: null,
		link,
		actionLabel: null,
	};
}

/** Faults are shown with a warning icon instead of the category icon. */
export function isFaultNotification(notification: AppNotification): boolean {
	return notification.type === DISPLAY_FAULT;
}
